import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, ChevronUp, Layers, Plus, RotateCcw, Save, ScanSearch, Search, Trash2, X } from 'lucide-react'
import styles from './PromptsTab.module.css'
import InputModal from '../../../ui/inputModal/InputModal'
import {
  promptTemplatesApi,
} from '../../../lib/apiClient'
import { toast } from '../../../lib/toast'
import { CreatePromptTemplateDto, PromptTemplate, PromptTemplateVariable } from '../../../types/types'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import CombinedPromptPanel from './CombinedPromptPanel'
import PromptAnalyzerPanel from './PromptAnalyzerPanel'
import { findSnippet, splitByQuery, templateMatches } from './promptSearch'
import { AnalyzerTemplate, ComposedPart, PromptTemplateGroup, PromptsView } from './types'

const COMPOSED_STORAGE_KEY = 'promptsTab.composedKeys'
const SEARCH_DEBOUNCE_MS = 200

function loadComposedKeys(): string[] {
  try {
    const raw = localStorage.getItem(COMPOSED_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {splitByQuery(text, query).map((part, i) =>
        part.isMatch ? <mark key={i}>{part.text}</mark> : <React.Fragment key={i}>{part.text}</React.Fragment>,
      )}
    </>
  )
}

// Built-in templates get their category from the server; user-created ones are "Custom"
const CUSTOM_CATEGORY = 'Custom'
const CATEGORY_ORDER = [
  'AI Tool Builder',
  'Chat – Base',
  'Chat – Regular Mode',
  'Chat – Driving Mode',
  'Data Lake – Instructions & Tool',
  'Data Lake – Tool Result Messages',
  'Tool Calls',
]

function getCategory(tpl: PromptTemplate): string {
  return tpl.category || CUSTOM_CATEGORY
}

function groupTemplatesByCategory(templates: PromptTemplate[]): PromptTemplateGroup[] {
  const byCategory = new Map<string, PromptTemplate[]>()
  for (const tpl of templates) {
    const category = getCategory(tpl)
    byCategory.set(category, [...(byCategory.get(category) ?? []), tpl])
  }
  const rank = (c: string) => {
    if (c === CUSTOM_CATEGORY) return Number.MAX_SAFE_INTEGER
    const i = CATEGORY_ORDER.indexOf(c)
    return i === -1 ? CATEGORY_ORDER.length : i
  }
  return [...byCategory.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([category, items]) => ({ category, items }))
}

// System tool definitions: parameters the server code reads - removing/renaming them disables features
const SYSTEM_TOOL_PARAMS: Record<string, string[]> = {
  studio_analyze_data_tool_definition: ['dataset_id', 'sql', 'export_to_excel', 'filename_label', 'return_full_rows'],
}

function parseVariables(raw: string | null): PromptTemplateVariable[] {
  if (!raw) return []
  try { return JSON.parse(raw) } catch { return [] }
}

function extractVarNames(content: string): string[] {
  const names: string[] = []
  const seen = new Set<string>()
  for (const [, name] of content.matchAll(/\{\{(\w+)\}\}/g)) {
    if (!seen.has(name)) { seen.add(name); names.push(name) }
  }
  return names
}

interface SelectionState {
  key: string
  text: string
  start: number
  end: number
  x: number
  y: number
}

const emptyForm: CreatePromptTemplateDto = { key: '', label: '', description: '', content: '' }

const PromptsTab: React.FC = () => {
  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState<CreatePromptTemplateDto>(emptyForm)
  const [creating, setCreating] = useState(false)
  const [selection, setSelection] = useState<SelectionState | null>(null)
  const [pendingConfirm, setPendingConfirm] = useState<{ type: 'reset' | 'delete'; key: string; label?: string } | null>(null)
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [composedKeys, setComposedKeys] = useState<string[]>(loadComposedKeys)
  const [view, setView] = useState<PromptsView>('prompts')

  useEffect(() => { loadTemplates() }, [])

  useEffect(() => {
    try { localStorage.setItem(COMPOSED_STORAGE_KEY, JSON.stringify(composedKeys)) } catch { /* storage unavailable */ }
  }, [composedKeys])

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [searchInput])

  // Searching opens every category and card that contains the text
  useEffect(() => {
    if (!search) return
    const cats: Record<string, boolean> = {}
    const cards: Record<string, boolean> = {}
    for (const tpl of templates) {
      if (templateMatches(tpl, drafts[tpl.key] ?? '', search)) {
        cats[getCategory(tpl)] = true
        cards[tpl.key] = true
      }
    }
    setOpenCategories((p) => ({ ...p, ...cats }))
    setExpanded((p) => ({ ...p, ...cards }))
    // drafts intentionally excluded: re-expanding on every keystroke in an editor would fight the user
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, templates])

  const toggleComposed = (key: string) => {
    setComposedKeys((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]))
  }

  const moveComposed = (key: string, direction: -1 | 1) => {
    setComposedKeys((p) => {
      const i = p.indexOf(key)
      const j = i + direction
      if (i === -1 || j < 0 || j >= p.length) return p
      const next = [...p]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }

  /** Opens a template's category + card, scrolls to it and focuses its editor (optionally selecting a range). */
  const jumpTo = (key: string, selectRange?: { start: number; end: number }) => {
    const tpl = templates.find((t) => t.key === key)
    if (!tpl) return
    if (search && !templateMatches(tpl, drafts[key] ?? '', search)) {
      setSearchInput(''); setSearch('')
    }
    setOpenCategories((p) => ({ ...p, [getCategory(tpl)]: true }))
    setExpanded((p) => ({ ...p, [key]: true }))
    requestAnimationFrame(() => requestAnimationFrame(() => {
      cardRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const ta = textareaRefs.current[key]
      if (ta) {
        ta.focus({ preventScroll: true })
        if (selectRange) ta.setSelectionRange(selectRange.start, selectRange.end)
      }
    }))
  }

  const analyzerTemplates: AnalyzerTemplate[] = useMemo(
    () => templates.map((t) => ({ key: t.key, label: t.label, content: drafts[t.key] ?? '' })),
    [templates, drafts],
  )

  const composedParts: ComposedPart[] = composedKeys
    .map((key) => templates.find((t) => t.key === key))
    .filter((t): t is PromptTemplate => !!t)
    .map((t) => ({
      key: t.key,
      label: t.label,
      content: drafts[t.key] ?? '',
      isDirty: (drafts[t.key] ?? '') !== saved[t.key],
    }))

  const loadTemplates = async () => {
    try {
      const data = await promptTemplatesApi.list()
      setTemplates(data)
      const d: Record<string, string> = {}
      const s: Record<string, string> = {}
      data.forEach((t) => { d[t.key] = t.content; s[t.key] = t.content })
      setDrafts(d); setSaved(s)
    } catch {
      toast.error('Failed to load prompt templates')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (key: string) => {
    setSaving((p) => ({ ...p, [key]: true }))
    try {
      const updated = await promptTemplatesApi.update(key, drafts[key])
      setSaved((p) => ({ ...p, [key]: updated.content }))
      toast.success('Prompt saved')
    } catch (err) {
      toast.error(err instanceof Error && err.message ? `Failed to save prompt: ${err.message}` : 'Failed to save prompt')
    }
    finally { setSaving((p) => ({ ...p, [key]: false })) }
  }

  const handleReset = (key: string) => {
    setPendingConfirm({ type: 'reset', key })
  }

  const handleDelete = (key: string, label: string) => {
    setPendingConfirm({ type: 'delete', key, label })
  }

  const handleConfirm = async () => {
    if (!pendingConfirm) return
    const { type, key, label } = pendingConfirm
    setPendingConfirm(null)
    if (type === 'reset') {
      setSaving((p) => ({ ...p, [key]: true }))
      try {
        const updated = await promptTemplatesApi.reset(key)
        setDrafts((p) => ({ ...p, [key]: updated.content }))
        setSaved((p) => ({ ...p, [key]: updated.content }))
        toast.success('Prompt reset to default')
      } catch { toast.error('Failed to reset prompt') }
      finally { setSaving((p) => ({ ...p, [key]: false })) }
    } else {
      try {
        await promptTemplatesApi.delete(key)
        setTemplates((p) => p.filter((t) => t.key !== key))
        toast.success('Prompt deleted')
      } catch { toast.error(`Failed to delete "${label}"`) }
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.key.trim() || !createForm.label.trim() || !createForm.content.trim()) {
      toast.error('Key, label, and content are required'); return
    }
    setCreating(true)
    try {
      const created = await promptTemplatesApi.create(createForm)
      setTemplates((p) => [...p, created])
      setDrafts((p) => ({ ...p, [created.key]: created.content }))
      setSaved((p) => ({ ...p, [created.key]: created.content }))
      setCreateForm(emptyForm); setShowCreateForm(false)
      toast.success('Prompt created')
    } catch { toast.error('Failed to create prompt - key may already exist') }
    finally { setCreating(false) }
  }

  const captureSelection = (key: string, ta: HTMLTextAreaElement, clientX: number, clientY: number) => {
    const start = ta.selectionStart
    const end = ta.selectionEnd
    if (start === end) { setSelection(null); return }
    setSelection({ key, text: ta.value.slice(start, end), start, end, x: clientX, y: clientY })
  }

  const handleMouseUp = (key: string, e: React.MouseEvent<HTMLTextAreaElement>) => {
    captureSelection(key, e.currentTarget, e.clientX, e.clientY)
  }

  const handleKeyUp = (key: string, e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!e.shiftKey && e.key !== 'End' && e.key !== 'Home') return
    const ta = e.currentTarget
    const rect = ta.getBoundingClientRect()
    captureSelection(key, ta, rect.left + rect.width / 2, rect.top + 24)
  }

  const applyWrap = (key: string) => {
    if (!selection || selection.key !== key) return
    const word = selection.text.trim()
    const content = drafts[key]
    const newContent = content.slice(0, selection.start) + `{{${word}}}` + content.slice(selection.end)
    setDrafts((p) => ({ ...p, [key]: newContent }))
    setSelection(null)
    const ta = textareaRefs.current[key]
    if (ta) { ta.focus(); const pos = selection.start + word.length + 4; ta.setSelectionRange(pos, pos) }
  }

  const applyUnwrap = (key: string) => {
    if (!selection || selection.key !== key) return
    const match = selection.text.match(/^\{\{(\w+)\}\}$/)
    if (!match) return
    const word = match[1]
    const content = drafts[key]
    const newContent = content.slice(0, selection.start) + word + content.slice(selection.end)
    setDrafts((p) => ({ ...p, [key]: newContent }))
    setSelection(null)
    const ta = textareaRefs.current[key]
    if (ta) { ta.focus(); const pos = selection.start + word.length; ta.setSelectionRange(pos, pos) }
  }

  if (loading) return <div className={styles.loading}>Loading prompt templates…</div>

  const visibleGroups = groupTemplatesByCategory(
    templates.filter((t) => templateMatches(t, drafts[t.key] ?? '', search)),
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>AI Prompt Templates</h2>
          <p className={styles.subtitle}>Customize the prompts used for AI-powered tool generation.</p>
        </div>
        {view === 'prompts' && (
          <button className={styles.addBtn} onClick={() => setShowCreateForm((v) => !v)}>
            {showCreateForm ? <X size={16} /> : <Plus size={16} />}
            {showCreateForm ? 'Cancel' : 'Add Prompt'}
          </button>
        )}
      </div>

      <div className={styles.viewTabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'prompts'}
          className={`${styles.viewTab} ${view === 'prompts' ? styles.viewTabActive : ''}`}
          onClick={() => setView('prompts')}
        >
          <Layers size={15} /> Prompts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'analyze'}
          className={`${styles.viewTab} ${view === 'analyze' ? styles.viewTabActive : ''}`}
          onClick={() => setView('analyze')}
        >
          <ScanSearch size={15} /> Analyze Prompt
        </button>
      </div>

      {view === 'prompts' && showCreateForm && (
        <form className={styles.createCard} onSubmit={handleCreate}>
          <h3 className={styles.createTitle}>New Prompt Template</h3>
          <div className={styles.formRow}>
            <label className={styles.label}>
              Key <span className={styles.required}>*</span>
              <input
                className={styles.textInput}
                value={createForm.key}
                onChange={(e) => setCreateForm((f: any) => ({ ...f, key: e.target.value.replace(/\s/g, '_').toLowerCase() }))}
                placeholder="my_custom_prompt"
                required
              />
            </label>
            <label className={styles.label}>
              Label <span className={styles.required}>*</span>
              <input
                className={styles.textInput}
                value={createForm.label}
                onChange={(e) => setCreateForm((f: any) => ({ ...f, label: e.target.value }))}
                placeholder="My Custom Prompt"
                required
              />
            </label>
          </div>
          <label className={styles.label}>
            Description
            <input
              className={styles.textInput}
              value={createForm.description ?? ''}
              onChange={(e) => setCreateForm((f: any) => ({ ...f, description: e.target.value }))}
              placeholder="What does this prompt do?"
            />
          </label>
          <label className={styles.label}>
            Content <span className={styles.required}>*</span>
            <textarea
              className={styles.contentTextarea}
              value={createForm.content}
              onChange={(e) => setCreateForm((f: any) => ({ ...f, content: e.target.value }))}
              rows={12}
              spellCheck={false}
              placeholder={`Enter your prompt template here.\nUse {{variableName}} for dynamic values.`}
              required
            />
          </label>
          <div className={styles.createActions}>
            <button type="submit" className={styles.saveBtn} disabled={creating}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <div className={styles.layout} hidden={view !== 'prompts'}>
        <div className={styles.mainColumn}>
          <SecureInput
            fieldType="text"
            preserveWhitespace
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search prompts by name, key or content…"
            icon={<Search size={16} />}
            autoComplete="off"
          />
          {search && visibleGroups.length === 0 && (
            <p className={styles.noResults}>No prompts contain “{search}”.</p>
          )}

          {visibleGroups.map(({ category, items }) => {
            const isOpen = openCategories[category] ?? false
            const hasDirty = items.some((t) => (drafts[t.key] ?? '') !== saved[t.key])
            return (
              <section key={category} className={styles.categorySection}>
                <button
                  type="button"
                  className={styles.categoryHeader}
                  onClick={() => setOpenCategories((p) => ({ ...p, [category]: !isOpen }))}
                  aria-expanded={isOpen}
                >
                  {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className={styles.categoryTitle}>{category}</span>
                  <span className={styles.categoryCount}>{items.length}</span>
                  {hasDirty && <span className={styles.dirtyDot} title="Unsaved changes in this category" />}
                </button>
                {isOpen && (
                  <div className={styles.list}>
                    {items.map((tpl) => {
                      const storedVars = parseVariables(tpl.variables)
                      const descMap = new Map(storedVars.map((v) => [v.name, v.description]))
                      const currentContent = drafts[tpl.key] ?? ''
                      const varNames = extractVarNames(currentContent)
                      const isBuiltIn = getCategory(tpl) !== CUSTOM_CATEGORY
                      const isDirty = currentContent !== saved[tpl.key]
                      const isExpanded = expanded[tpl.key] ?? false

                      const sel = selection?.key === tpl.key ? selection : null
                      const selIsWrapped = sel ? /^\{\{(\w+)\}\}$/.test(sel.text) : false
                      const selIsWrapable = sel ? /^\w+$/.test(sel.text.trim()) && !selIsWrapped : false
                      const showTooltip = sel && (selIsWrapped || selIsWrapable)

                      const isComposed = composedKeys.includes(tpl.key)
                      const snippet = search ? findSnippet(currentContent, search) : null

                      return (
                        <div
                          key={tpl.key}
                          ref={(el) => { cardRefs.current[tpl.key] = el }}
                          className={`${styles.card} ${isComposed ? styles.cardComposed : ''}`}
                        >
                          <div
                            className={styles.cardHeader}
                            onClick={() => setExpanded((p) => ({ ...p, [tpl.key]: !isExpanded }))}
                          >
                            <div className={styles.cardHeaderLeft}>
                              <input
                                type="checkbox"
                                className={styles.composeCheckbox}
                                checked={isComposed}
                                onChange={() => toggleComposed(tpl.key)}
                                onClick={(e) => e.stopPropagation()}
                                title="Add to combined prompt"
                                aria-label={`Add ${tpl.label} to combined prompt`}
                              />
                              <span className={styles.cardLabel}><Highlighted text={tpl.label} query={search} /></span>
                              {isBuiltIn && <span className={styles.builtInBadge}>Built-in</span>}
                              {isDirty && <span className={styles.dirtyDot} title="Unsaved changes" />}
                            </div>
                            <button className={styles.collapseBtn} type="button" aria-label="Toggle">
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </div>

                          {snippet && (
                            <button
                              type="button"
                              className={styles.searchSnippet}
                              onClick={() => jumpTo(tpl.key, { start: snippet.index, end: snippet.index + snippet.match.length })}
                              title="Jump to this text in the editor"
                            >
                              {snippet.before}<mark>{snippet.match}</mark>{snippet.after}
                            </button>
                          )}

                          {isExpanded && (
                            <div className={styles.cardBody}>
                              {tpl.description && <p className={styles.cardDescription}>{tpl.description}</p>}

                              {SYSTEM_TOOL_PARAMS[tpl.key] && (
                                <div className={styles.systemNote}>
                                  <AlertTriangle size={14} className={styles.systemNoteIcon} />
                                  <span>
                                    System parameters:{' '}
                                    {SYSTEM_TOOL_PARAMS[tpl.key].map((name, i) => (
                                      <React.Fragment key={name}>
                                        {i > 0 && ', '}
                                        <code>{name}</code>
                                      </React.Fragment>
                                    ))}{' '}
                                    are used by the code. Removing or renaming them can disable features (e.g. Excel export).
                                    The tool name is fixed and cannot be changed.
                                  </span>
                                </div>
                              )}

                              {varNames.length > 0 && (
                                <div className={styles.variablesRow}>
                                  <span className={styles.variablesLabel}>Variables:</span>
                                  {varNames.map((name) => (
                                    <span key={name} className={styles.chip} title={descMap.get(name) ?? name}>
                                      {`{{${name}}}`}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {showTooltip && (
                                <div
                                  className={styles.floatingTooltip}
                                  style={{ top: sel.y - 48, left: sel.x }}
                                  onMouseDown={(e) => e.preventDefault()}
                                >
                                  {selIsWrapable && (
                                    <button className={styles.tooltipBtn} onClick={() => applyWrap(tpl.key)}>
                                      Make dynamic
                                      <code className={styles.tooltipCode}>{`{{${sel.text.trim()}}}`}</code>
                                    </button>
                                  )}
                                  {selIsWrapped && (
                                    <button className={styles.tooltipBtn} onClick={() => applyUnwrap(tpl.key)}>
                                      Unwrap to
                                      <code className={styles.tooltipCode}>{sel.text.slice(2, -2)}</code>
                                    </button>
                                  )}
                                  <span className={styles.tooltipArrow} />
                                </div>
                              )}

                              <textarea
                                ref={(el) => { textareaRefs.current[tpl.key] = el }}
                                className={styles.contentTextarea}
                                value={currentContent}
                                onChange={(e) => setDrafts((p) => ({ ...p, [tpl.key]: e.target.value }))}
                                onMouseUp={(e) => handleMouseUp(tpl.key, e)}
                                onKeyUp={(e) => handleKeyUp(tpl.key, e)}
                                onBlur={() => setSelection(null)}
                                rows={20}
                                spellCheck={false}
                              />

                              <div className={styles.cardActions}>
                                <button
                                  className={styles.saveBtn}
                                  onClick={() => handleSave(tpl.key)}
                                  disabled={!isDirty || saving[tpl.key]}
                                >
                                  <Save size={14} />
                                  {saving[tpl.key] ? 'Saving…' : 'Save'}
                                </button>

                                {isBuiltIn && (
                                  <button
                                    className={styles.resetBtn}
                                    onClick={() => handleReset(tpl.key)}
                                    disabled={saving[tpl.key]}
                                  >
                                    <RotateCcw size={14} />
                                    Reset to default
                                  </button>
                                )}

                                {!isBuiltIn && (
                                  <button
                                    className={styles.deleteBtn}
                                    onClick={() => handleDelete(tpl.key, tpl.label)}
                                  >
                                    <Trash2 size={14} />
                                    Delete
                                  </button>
                                )}
                              </div>

                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>
            )
          })}
        </div>

        <aside className={styles.sidePanel}>
          <CombinedPromptPanel
            parts={composedParts}
            onJump={(key) => jumpTo(key)}
            onMove={moveComposed}
            onRemove={(key) => setComposedKeys((p) => p.filter((k) => k !== key))}
            onClear={() => setComposedKeys([])}
          />
        </aside>
      </div>

      {/* Kept mounted (hidden) so the pasted prompt and its analysis survive switching views */}
      <div hidden={view !== 'analyze'}>
        <PromptAnalyzerPanel
          templates={analyzerTemplates}
          onJump={(key) => { setView('prompts'); jumpTo(key) }}
        />
      </div>

      <InputModal
        open={pendingConfirm?.type === 'reset'}
        title="Reset to Default"
        message="Reset to built-in default? Custom content will be lost."
        confirmText="Reset"
        cancelText="Cancel"
        showInput={false}
        onConfirm={handleConfirm}
        onCancel={() => setPendingConfirm(null)}
      />
      <InputModal
        open={pendingConfirm?.type === 'delete'}
        title="Delete Prompt"
        message={`Delete "${pendingConfirm?.label}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        showInput={false}
        danger
        onConfirm={handleConfirm}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  )
}

export default PromptsTab
